import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { Transition } from 'react-transition-group'

import { useAppLayout } from '@/components/AppLayout/AppLayoutProvider'
import PopinCardStep from '@/components/Card/PopinCardStep'
import { useGSAP } from '@/composables/useGSAP'
import { useRunnerState } from '@/composables/useRunnerState'

export type PopinCardProps = {
  persona: string
  scenario: number
  popin: string
  isOpen?: boolean
  onClose?: () => void
  hide?: boolean
}

export default function PopinCard({
  persona,
  scenario,
  popin,
  isOpen = false,
  onClose = () => {},
  hide = false,
}: PopinCardProps) {
  const id = useId()
  const { t, i18n } = useTranslation()

  const { root } = useAppLayout()
  const { gsap } = useGSAP()
  const { actor, context } = useRunnerState()

  const gamePopinAudio = context.game.popinAudio
  const steps = parseInt(t(`runner.${persona}.${scenario + 1}.${popin}.steps`)) || 1
  const [currentStep, setCurrentStep] = useState(0)
  const [exiting, setExiting] = useState(true)
  const [playAudio, setPlayAudio] = useState(false)
  const [hideCta, setHideCta] = useState(false)

  const nodeRef = useRef<HTMLDivElement>(null)
  const elementRef = useRef<HTMLDivElement>(null)
  const veilRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isOpen) {
      setExiting(false)
    }
  }, [isOpen])

  const onExit = useCallback(() => {
    const ctx = gsap.context(() => {
      const timeline = gsap.timeline({
        onComplete: () => {
          onClose?.()
        },
      })

      const background = nodeRef.current?.querySelector('.PopinCardStep__Background')
      if (background) {
        timeline.to(
          background,
          {
            scale: 0,
            rotate: currentStep % 2 === 0 ? 90 : -90,
            duration: 0.5,
            ease: 'power1.out',
          },
          0
        )
      }
      const content = nodeRef.current?.querySelector('.PopinCardStep__Content')
      if (content) {
        timeline.to(
          content,
          {
            opacity: 0,
            duration: 0.3,
            ease: 'power1.out',
          },
          0
        )
      }

      timeline.fromTo(
        veilRef.current,
        {
          opacity: 1,
        },
        {
          opacity: 0,
          duration: 0.3,
          ease: 'power2.out',
        },
        0.2
      )
      timeline.set(elementRef.current, {
        visibility: 'hidden',
        immediateRender: false,
      })
    })

    return () => ctx.kill()
  }, [gsap, nodeRef, onClose, currentStep])

  const onInnerNext = useCallback(() => {
    setPlayAudio(false)
    if (currentStep < steps - 1) {
      setCurrentStep(currentStep + 1)
      setHideCta(false)
    } else {
      setExiting(true)
    }
  }, [currentStep, steps, setCurrentStep, setExiting])
  const onNext = useCallback(() => {
    // console.log('[PopinCard] onNext');
    const audioKey = `runner.${persona}.${scenario + 1}.${popin}.step.${currentStep + 1}.audio`
    const popinAudio = i18n.exists(audioKey) ? t(audioKey) : null

    if (popinAudio !== null) {
      if (!playAudio) {
        setPlayAudio(true)
        setHideCta(true)
        actor.send({ type: 'GAME_STATE', value: { popinAudio: popinAudio } })
      }
    } else {
      onInnerNext()
    }
  }, [onInnerNext, playAudio, actor, persona, scenario, popin, currentStep, i18n, t])

  useEffect(() => {
    if (isOpen && !exiting && playAudio && gamePopinAudio === null) {
      onInnerNext()
    }
  }, [gamePopinAudio, playAudio, isOpen, exiting, onInnerNext])

  const onEnter = useCallback(() => {
    const ctx = gsap.context(() => {
      setCurrentStep(0)
      setHideCta(false)
      if (veilRef.current) {
        gsap.timeline().fromTo(
          veilRef.current,
          {
            opacity: 0,
          },
          {
            opacity: 1,
            duration: 0.5,
            ease: 'power2.out',
          },
          0
        )
      }
    })

    return () => ctx.kill()
  }, [gsap, veilRef, setCurrentStep])

  return (
    root.current &&
    createPortal(
      <Transition
        in={isOpen && !exiting}
        nodeRef={elementRef}
        onEnter={onEnter}
        onExit={onExit}
        unmountOnExit
        mountOnEnter
        appear
        timeout={{
          enter: 500,
          exit: 500,
        }}
      >
        <div
          ref={elementRef}
          role='dialog'
          aria-modal='true'
          className={`PopinCard fixed inset-0 pointer-events-none z-90 transition-opacity duration-500 ease-out-quad ${hide ? 'opacity-0' : ''}`}
        >
          <div ref={veilRef} className='RunnerUIPauseBackground absolute top-0 left-0 w-full h-full bg-black/30 z-1' />
          <div className='relative w-full h-full content-center container py-20 z-2 lg:mt-40'>
            <div className='relative col-start-1 col-span-10 md:col-start-3 md:col-span-6 lg:col-start-4 lg:col-span-6 pointer-events-none grid'>
              {popin && (
                <PopinCardStep
                  ref={nodeRef}
                  className='relative col-start-2 col-span-8 lg:col-start-1 lg:col-span-1 row-start-1 row-span-1 max-w-690 justify-self-center w-full'
                  key={currentStep}
                  id={id}
                  baseI18nKey={`runner.${persona}.${scenario + 1}.${popin}.step.${currentStep + 1}`}
                  step={currentStep}
                  isLast={currentStep === steps - 1}
                  ctaI18nKey='runner.cta'
                  onNext={onNext}
                  hideCta={hideCta}
                />
              )}
            </div>
          </div>
        </div>
      </Transition>,
      root.current
    )
  )
}
